/**
 * Crew Member Templates — reusable Member definitions (Run 3+)
 *
 * A TEMPLATE Crew Member is code-defined: Mate face + standing mandate +
 * tool policy + default Space. It is NOT a Helper row bind (C3 Helper bind
 * remains NOT_BOUND_YET). Template bind persists on the Space via
 * `custom_instructions` (C1 injection) + optional `display_preferences`
 * marker so roster / docs can resolve the bound Member.
 *
 * First templates: Sales Agent (Second Shift), Closer's Room (Run 9),
 * BD Outbound / Kim (Run 10 scaffold).
 *
 * Honesty:
 * - Do not claim Helper → Crew Member bind from this module
 * - Do not claim per-Member tool allowlists are fully enforced server-side
 *   for every family — see toolPolicy + docs/crew/CREW_MEMBER_BINDING.md
 * External sends:
 * - Chat Custom Rules `send=Never` strips outbound tools (Run 5)
 * - Runner TIER 1 may auto-send Day 0/5/7 approved templates only (Sales Agent)
 *
 * Preserve LosiAnimated. No Muse/Vesper.
 */

import type { MateId } from '@/lib/crew/mates'
import { MATE_BY_ID, resolveMateId } from '@/lib/crew/mates'
import { SPACE_STANDING_INSTRUCTIONS_MAX_CHARS } from '@/lib/spaces/standing-instructions'

/** Ask = escalate for approval; Never = do not attempt; Allow = silent OK. */
export type CrewMemberTemplateToolMode = 'ask' | 'allow' | 'never'

export type CrewMemberTemplateToolPolicy = {
  /** SMS / email / any external message channel */
  send: CrewMemberTemplateToolMode
  /** Purchases, billing, payments */
  buy: CrewMemberTemplateToolMode
  /** Destructive deletes */
  delete: CrewMemberTemplateToolMode
  /** Cloud computer / browser mutate */
  computer: CrewMemberTemplateToolMode
  /** Calendar create/update/book (read may still be allowed) */
  calendar_write: CrewMemberTemplateToolMode
  /** Nexus CRM reads */
  crm_read: CrewMemberTemplateToolMode
  /** Nexus CRM writes that are not outbound sends (log/note/status) */
  crm_write: CrewMemberTemplateToolMode
  /** Web search / research */
  web_search: CrewMemberTemplateToolMode
  /** Space tasks/notes mutate */
  space_write: CrewMemberTemplateToolMode
}

export type CrewMemberTemplateId =
  | 'sales-agent-second-shift'
  | 'closers-room'
  | 'bd-outbound'

export type CrewMemberTemplate = {
  id: CrewMemberTemplateId
  /** Catalog Mate face (Fox · Second Shift in Perfect Crew plan). */
  mateId: MateId
  displayName: string
  roleLabel: string
  /** One plain line — what this mate does for the owner. */
  jobLine: string
  /** Default Space to bind (Losi Growth). */
  defaultSpaceId: string
  defaultSpaceName: string
  /** Full standing mandate — must include no-Sunday + escalate-on-send. */
  standingInstructions: string
  toolPolicy: CrewMemberTemplateToolPolicy
  /** Human-readable context pointers (playbook lives in Space, not repo). */
  contextPointers: string[]
  /** What this Member may do without asking (intent; runtime gates may still confirm). */
  silentOps: string[]
  /** What must wait for Ayo approval. */
  escalateOps: string[]
}


/** Losi Growth Space — Project Autonomous Inc / Second Shift home. */
export const LOSI_GROWTH_SPACE_ID =
  '913434f3-8759-49da-a059-1bbeb17ca3df' as const

export const CREW_MEMBER_TEMPLATE_MARKER_PREFIX =
  '<!-- losi-crew-member-template:' as const

/**
 * Sales Agent — Second Shift nurturing sequence (Run 3).
 * Bound to Losi Growth. Preparation autonomous; execution gated.
 */
export const SALES_AGENT_SECOND_SHIFT_STANDING_INSTRUCTIONS = `You are the Sales Agent for Losi. You own the Second Shift nurturing sequence — a 7-day multi-touch outreach system for local service businesses.

YOUR SEQUENCE:
- Day 0: Log call attempt. If no answer, prepare personalized text within 5 min.
- Day 1: Prepare email with Loom video recommendation (their business name + specific pain point).
- Day 3: Flag for second call attempt OR field visit if Ottawa local.
- Day 5: Prepare relevance hook (pull latest Google review signal for that business).
- Day 7: Prepare close-the-loop text.
- Day 14+: Flag for monthly seasonal trigger check-in.

WHAT YOU CAN DO SILENTLY (no approval needed):
- Log interactions in Nexus CRM
- Research prospects (web search, Google reviews, social)
- Update lead status and notes
- Prepare draft messages (but NOT send them)
- Schedule internal follow-up reminders
- Move tasks and update Space items
- Monitor signals and flag interesting patterns

WHAT REQUIRES ESCALATION (must wait for Ayo's approval):
- ANY message sent to a real person (text, email, any channel)
- ANY pricing or offer communication
- ANY calendar booking that commits Ayo's time
- ANY action outside this defined mandate

CONSTRAINTS:
- Never send unapproved copy. Ever.
- Never contact anyone on Sunday.
- Never guess at pricing — escalate.
- When in doubt, prepare but don't execute.

Your job is to make the nurturing sequence effortless for Ayo.
He should wake up to prepared drafts, researched prospects, and clear recommendations — not decisions you made for him.

Be autonomous in preparation. Be gated on execution.

CONTEXT (read from this Space — do not invent missing playbook data):
- SDR Pod Playbook and Growth Space notes/sheets hold hit lists, approved copy banks, and call windows.
- Prefer Space notes / sheets / CRM over guessing prospect details.
- External-send crons stay dark until template-only send gates exist.`

export const SALES_AGENT_SECOND_SHIFT_TOOL_POLICY: CrewMemberTemplateToolPolicy =
  {
    send: 'ask',
    buy: 'never',
    delete: 'ask',
    computer: 'ask',
    calendar_write: 'never',
    crm_read: 'allow',
    crm_write: 'allow',
    web_search: 'allow',
    space_write: 'allow'
  }

export const SALES_AGENT_SECOND_SHIFT_TEMPLATE: CrewMemberTemplate = {
  id: 'sales-agent-second-shift',
  mateId: 'fox',
  displayName: 'Sales Agent',
  roleLabel: 'Sales · Second Shift',
  jobLine: 'Follows up with new leads and drafts outreach — asks before sending.',
  defaultSpaceId: LOSI_GROWTH_SPACE_ID,
  defaultSpaceName: 'Losi Growth',
  standingInstructions: SALES_AGENT_SECOND_SHIFT_STANDING_INSTRUCTIONS,
  toolPolicy: SALES_AGENT_SECOND_SHIFT_TOOL_POLICY,
  contextPointers: [
    'Losi Growth Space notes (SDR Pod Playbook)',
    'Growth Space sheets / hit lists (prospect rows)',
    'Nexus CRM leads/contacts/activities for Second Shift prospects',
    'Approved text/email templates when present in Space — draft only until send gates'
  ],
  silentOps: [
    'research_prospects',
    'log_crm_interactions',
    'update_lead_notes_status',
    'prepare_draft_messages',
    'create_space_follow_up_tasks',
    'monitor_signals'
  ],
  escalateOps: [
    'send_sms',
    'send_email',
    'any_external_message',
    'pricing_or_offer_comms',
    'calendar_booking',
    'actions_outside_mandate'
  ]
}

/**
 * Closer's Room — Michael / close-path Member (Run 9 scaffold).
 * Receives lead_transfer handoffs; prepares close work; gated on sends.
 * Runner cron path still Sales-Agent-only — do not claim auto-close.
 */
export const CLOSERS_ROOM_STANDING_INSTRUCTIONS = `You are Closer's Room for Losi — the close-path Crew Member. You own qualified opportunities after BD / Sales handoff (Run 8 lead_transfer).

YOUR MANDATE:
- Intake handed-off leads with full context (notes, CRM refs) — never invent missing facts.
- Prepare close briefs: decision-maker map, objections, next conversation goal.
- Draft follow-ups and proposals for approval — do NOT send.
- Flag stale close opportunities and recommend next human touch.
- Accept inbound intel_share / lead_transfer context; keep audit notes in CRM.

WHAT YOU CAN DO SILENTLY (no approval needed):
- Read Nexus CRM for the handed-off lead/contact/company
- Research the account (web search, reviews, public signals)
- Log close notes and update opportunity status fields that are not outbound sends
- Prepare draft messages and close briefs in the Space
- Create Space follow-up tasks for the owner

WHAT REQUIRES ESCALATION (must wait for owner approval):
- ANY message sent to a real person (text, email, any channel)
- ANY pricing, discount, or contractual commitment
- ANY calendar booking that commits the owner's time
- ANY action outside this close-path mandate
- Accepting a handoff that lacks a permission grant (system-enforced)

CONSTRAINTS:
- Never send unapproved copy. Ever.
- Never contact anyone on Sunday.
- Never guess at pricing — escalate.
- Never silently access another crew's data — wait for an explicit handoff grant.
- When in doubt, prepare but don't execute.

Be autonomous in preparation. Be gated on execution.

CONTEXT (read from this Space — do not invent missing playbook data):
- Growth Space notes/sheets and CRM hold the account truth.
- Handoff receipts (Crew handoff notes) supply inbound context.
- External-send crons stay dark (RUN2-DARK) — Closer's Room has no auto-send runner yet.`

export const CLOSERS_ROOM_TOOL_POLICY: CrewMemberTemplateToolPolicy = {
  send: 'ask',
  buy: 'never',
  delete: 'ask',
  computer: 'ask',
  calendar_write: 'never',
  crm_read: 'allow',
  crm_write: 'allow',
  web_search: 'allow',
  space_write: 'allow'
}

export const CLOSERS_ROOM_TEMPLATE: CrewMemberTemplate = {
  id: 'closers-room',
  mateId: 'wolf',
  displayName: "Closer's Room",
  roleLabel: 'Close · Closers Room',
  jobLine: 'Takes handed-off leads and prepares close work — asks before sending.',
  defaultSpaceId: LOSI_GROWTH_SPACE_ID,
  defaultSpaceName: 'Losi Growth',
  standingInstructions: CLOSERS_ROOM_STANDING_INSTRUCTIONS,
  toolPolicy: CLOSERS_ROOM_TOOL_POLICY,
  contextPointers: [
    'Losi Growth Space notes (close briefs / handoff receipts)',
    'Nexus CRM leads/contacts for handed-off opportunities',
    'Run 8 crew handoff notes (lead_transfer / intel_share)',
    'Approved close copy when present in Space — draft only'
  ],
  silentOps: [
    'intake_handoff_context',
    'research_account',
    'log_crm_close_notes',
    'prepare_close_briefs',
    'prepare_draft_messages',
    'create_space_follow_up_tasks'
  ],
  escalateOps: [
    'send_sms',
    'send_email',
    'any_external_message',
    'pricing_or_contract_comms',
    'calendar_booking',
    'actions_outside_mandate'
  ]
}

/**
 * BD Outbound — Kim / business-development Member (Run 10 scaffold).
 * Finds and qualifies outbound opportunities; hands off via Run 8 lead_transfer.
 * No auto-send runner — same honesty as Closer's Room.
 */
export const BD_OUTBOUND_STANDING_INSTRUCTIONS = `You are BD Outbound for Losi — Kim's business-development Crew Member. You own outbound prospecting and qualification before close handoff.

YOUR MANDATE:
- Build and refresh outbound hit lists from Space sheets + CRM (local service businesses and ICP fits).
- Research prospects (web, reviews, public signals) and score fit honestly — never invent metrics.
- Prepare cold/warm outreach drafts for approval — do NOT send.
- When a lead is sales-ready, create a Run 8 lead_transfer handoff to Closer's Room (requires an active permission grant).
- Keep BD notes and next actions current in CRM and the Space.

WHAT YOU CAN DO SILENTLY (no approval needed):
- Research prospects and accounts
- Log BD interactions and update lead notes/status (non-send)
- Prepare draft outreach and qualification briefs
- Create Space follow-up tasks
- Draft handoff payloads (business data only — never secrets)

WHAT REQUIRES ESCALATION (must wait for owner approval):
- ANY message sent to a real person (text, email, any channel)
- ANY pricing or offer communication
- ANY calendar booking that commits the owner's time
- ANY action outside this BD mandate
- Cross-crew handoff without an active permission grant (system-enforced)

CONSTRAINTS:
- Never send unapproved copy. Ever.
- Never contact anyone on Sunday.
- Never guess at pricing — escalate.
- Never silently read another crew's private notes — use handoffs + grants.
- When in doubt, prepare but don't execute.

Be autonomous in preparation. Be gated on execution.

CONTEXT (read from this Space — do not invent missing playbook data):
- Growth Space SDR / BD notes and sheets hold ICP and hit lists.
- Nexus CRM is source of truth for lead state.
- External-send crons stay dark (RUN2-DARK) — BD Outbound has no auto-send runner yet.`

export const BD_OUTBOUND_TOOL_POLICY: CrewMemberTemplateToolPolicy = {
  send: 'ask',
  buy: 'never',
  delete: 'ask',
  computer: 'ask',
  calendar_write: 'never',
  crm_read: 'allow',
  crm_write: 'allow',
  web_search: 'allow',
  space_write: 'allow'
}

export const BD_OUTBOUND_TEMPLATE: CrewMemberTemplate = {
  id: 'bd-outbound',
  mateId: 'otter',
  displayName: 'BD Outbound',
  roleLabel: 'BD · Outbound',
  jobLine: 'Finds and qualifies outbound prospects — asks before sending.',
  defaultSpaceId: LOSI_GROWTH_SPACE_ID,
  defaultSpaceName: 'Losi Growth',
  standingInstructions: BD_OUTBOUND_STANDING_INSTRUCTIONS,
  toolPolicy: BD_OUTBOUND_TOOL_POLICY,
  contextPointers: [
    'Losi Growth Space notes/sheets (ICP + hit lists)',
    'Nexus CRM leads/contacts for outbound prospects',
    'Run 8 handoffs (lead_transfer → Closers Room)',
    'Approved outreach copy when present — draft only'
  ],
  silentOps: [
    'research_prospects',
    'score_icp_fit',
    'log_crm_bd_notes',
    'prepare_outreach_drafts',
    'prepare_handoff_payload',
    'create_space_follow_up_tasks'
  ],
  escalateOps: [
    'send_sms',
    'send_email',
    'any_external_message',
    'pricing_or_offer_comms',
    'calendar_booking',
    'actions_outside_mandate'
  ]
}

export const CREW_MEMBER_TEMPLATES: Record<
  CrewMemberTemplateId,
  CrewMemberTemplate
> = {
  'sales-agent-second-shift': SALES_AGENT_SECOND_SHIFT_TEMPLATE,
  'closers-room': CLOSERS_ROOM_TEMPLATE,
  'bd-outbound': BD_OUTBOUND_TEMPLATE
}

export function listCrewMemberTemplates(): CrewMemberTemplate[] {
  return Object.values(CREW_MEMBER_TEMPLATES)
}

export function getCrewMemberTemplate(
  id: string | null | undefined
): CrewMemberTemplate | null {
  if (!id || typeof id !== 'string') return null
  return CREW_MEMBER_TEMPLATES[id as CrewMemberTemplateId] ?? null
}

/** Owner-facing job line for a template id (mate cards). */
export function crewMemberTemplateJobLine(
  templateId: string | null | undefined
): string {
  const t = getCrewMemberTemplate(templateId)
  return (
    t?.jobLine ||
    'Works on your crew and asks before anything that matters.'
  )
}

export function resolveCrewMemberTemplateMateId(
  template: CrewMemberTemplate
): MateId {
  const resolved = resolveMateId(template.mateId)
  if (resolved && MATE_BY_ID.has(resolved)) return resolved
  return 'fox'
}

/** Machine marker embedded at top of Space custom_instructions. */
export function buildCrewMemberTemplateMarker(
  template: CrewMemberTemplate
): string {
  return `${CREW_MEMBER_TEMPLATE_MARKER_PREFIX}${template.id} mate:${template.mateId} name:${template.displayName} -->`
}

export function parseCrewMemberTemplateIdFromInstructions(
  customInstructions: string | null | undefined
): CrewMemberTemplateId | null {
  if (typeof customInstructions !== 'string' || !customInstructions.trim()) {
    return null
  }
  const match = customInstructions.match(
    /<!--\s*losi-crew-member-template:([a-z0-9-]+)\b/i
  )
  if (!match?.[1]) return null
  return getCrewMemberTemplate(match[1])?.id ?? null
}

/**
 * Compose Space custom_instructions for a template bind.
 * Keeps body under Space standing-instructions max (C1).
 */
export function composeCrewMemberTemplateStandingInstructions(
  template: CrewMemberTemplate,
  options?: { preserveExtra?: string | null }
): string {
  const marker = buildCrewMemberTemplateMarker(template)
  const header = [
    marker,
    `CREW MEMBER TEMPLATE: ${template.displayName} (${template.roleLabel})`,
    `Mate: ${template.mateId} · Space: ${template.defaultSpaceName}`
  ].join('\n')

  const extra =
    typeof options?.preserveExtra === 'string'
      ? options.preserveExtra.trim()
      : ''
  const body = [header, '', template.standingInstructions.trim()]
  if (extra && !extra.includes(template.standingInstructions.trim().slice(0, 80))) {
    body.push('', '---', 'ADDITIONAL SPACE NOTES:', extra)
  }

  const joined = body.join('\n').trim()
  if (joined.length <= SPACE_STANDING_INSTRUCTIONS_MAX_CHARS) return joined

  return `${joined.slice(0, SPACE_STANDING_INSTRUCTIONS_MAX_CHARS - 80)}\n[truncated: standing instructions exceeded ${SPACE_STANDING_INSTRUCTIONS_MAX_CHARS} characters]`
}

/** Hard invariants every outbound / client-facing template must keep. */
export function assertCrewMemberTemplateSafety(
  template: CrewMemberTemplate
): string[] {
  const errors: string[] = []
  const text = template.standingInstructions.toLowerCase()

  if (!text.includes('sunday')) {
    errors.push('standing instructions must include no-Sunday constraint')
  }
  if (template.toolPolicy.send === 'allow') {
    errors.push('send must not be allow until template-only send gates exist')
  }
  if (template.toolPolicy.buy !== 'never') {
    errors.push('buy must be never for TEMPLATE Members without finance mandate')
  }
  if (template.toolPolicy.calendar_write !== 'never') {
    errors.push('calendar_write must be never until booking gates exist')
  }
  if (
    !template.escalateOps.some(op =>
      /send|email|sms|external|message/i.test(op)
    )
  ) {
    errors.push('escalateOps must include external send channels')
  }
  if (!getCrewMemberTemplate(template.id)) {
    errors.push('template id missing from registry')
  }
  return errors
}
